import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import MerchantLogin from './MerchantLogin';
import MerchantRegister from './MerchantRegister';
import TokenManagement from './TokenManagement';
import Qrcode from './Qrcode';
import Land from './Land';
import PaymentTerminal from './components/terminal/PaymentTerminal';
import { updateManifestLink } from './pwaUtils';
import AnalyticsDashboard from './AnalyticsDashboard';

// ✅ NEW: Import Reown config to initialize AppKit
import './config/reownConfig';

import './Land.css';
import './styles/terminal/WalletConnect.css';
import './styles/terminal/QRScanner.css';
import './styles/terminal/PaymentTerminal.css';
import './styles/terminal/ChainSelector.css';
import './styles/terminal/TokenBalanceFetcher.css';
import './styles/terminal/PaymentDetails.css';

const RouteChangeHandler = () => {
  const location = useLocation();

  useEffect(() => {
    updateManifestLink();
  }, [location.pathname]);

  return null;
};

const MerchantPanel = () => {
  const [screen, setScreen] = useState('checking');
  const [merchantData, setMerchantData] = useState(null);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    const sessionToken = localStorage.getItem('merchant_session');
    const merchantWallet = localStorage.getItem('merchant_wallet');

    if (!sessionToken || !merchantWallet) {
      setScreen('login');
      return;
    }

    try {
      const response = await fetch('https://web3-pos-v2.gdbok.workers.dev/api/merchant/verify-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken })
      });

      const result = await response.json();

      if (result.valid) {
        setMerchantData({ wallet: merchantWallet, sessionToken });
        setScreen('dashboard');
      } else {
        localStorage.removeItem('merchant_session');
        localStorage.removeItem('merchant_wallet');
        setScreen('login');
      }
    } catch (err) {
      console.error('Session check error:', err);
      setScreen('login');
    }
  };

  const handleLoginSuccess = (data) => {
    setMerchantData(data);
    setScreen('dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('merchant_session');
    localStorage.removeItem('merchant_wallet');
    setMerchantData(null);
    setScreen('login');
  };

  if (screen === 'checking') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔄</div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (screen === 'login') {
    return (
      <MerchantLogin
        onLoginSuccess={handleLoginSuccess}
        onSwitchToRegister={() => setScreen('register')}
      />
    );
  }

  if (screen === 'register') {
    return (
      <MerchantRegister
        onRegisterSuccess={() => setScreen('login')}
        onSwitchToLogin={() => setScreen('login')}
      />
    );
  }

  if (screen === 'dashboard' && merchantData) {
    if (window.location.hash === '#/analytics') {
      return (
        <AnalyticsDashboard
          merchantWallet={merchantData.wallet}
          onBack={() => {
            window.location.hash = '';
            setScreen('dashboard');
          }}
        />
      );
    }
    
    return (
      <TokenManagement
        merchantWallet={merchantData.wallet}
        onLogout={handleLogout}
      />
    );
  }

  return null;
};

function App() {
  return (
    <Router>
      <RouteChangeHandler />
      <Routes>
        <Route path="/" element={<Land />} />
        <Route path="/merchant" element={<MerchantPanel />} />
        <Route path="/collect" element={<Qrcode />} />
        <Route path="/pay" element={<PaymentTerminal />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;